import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Avatar, Menu, MenuItem, Divider, ListItemIcon, ListItemText } from '@mui/material';
import {
    Notifications as NotificationsIcon,
    ElectricBolt,
    KeyboardArrowDown,
    Person,
    ExitToApp,
    Menu as MenuIcon
} from '@mui/icons-material';
import { useGetIdentity, useLogout, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { usePublicSettings } from '../../hooks/usePublicSettings';

interface CustomAppBarProps {
    isMobile?: boolean;
    onMenuToggle?: () => void;
}

export const CustomAppBar = ({ isMobile = false, onMenuToggle }: CustomAppBarProps) => {
    const { data: identity } = useGetIdentity();
    const logout = useLogout();
    const navigate = useNavigate();
    const translate = useTranslate();
    const { settings: companySettings } = usePublicSettings();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleProfile = () => {
        navigate('/profile');
        handleMenuClose();
    };

    const handleLogout = () => {
        logout();
        handleMenuClose();
    };

    return (
        <AppBar
            position="fixed"
            sx={{
                zIndex: 1201,
                backgroundColor: '#FFFFFF',
                color: '#1A1A1A',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}
        >
            <Toolbar sx={{ height: 72, px: isMobile ? 1 : 3 }}>
                {isMobile && (
                    <IconButton
                        onClick={onMenuToggle}
                        sx={{
                            mr: 1,
                            color: '#606060',
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                )}

                {/* Logo and Brand */}
                <Box sx={{ display: 'flex', alignItems: 'center', mr: isMobile ? 'auto' : 4 }}>
                    {companySettings.companyLogo ? (
                        <Box
                            component="img"
                            src={companySettings.companyLogo}
                            alt={companySettings.companyName}
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                objectFit: 'contain',
                                mr: isMobile ? 0 : 2,
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #009A75 0%, #00BF8F 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: isMobile ? 0 : 2,
                                boxShadow: '0 4px 12px rgba(0,154,117,0.3)',
                            }}
                        >
                            <ElectricBolt sx={{ color: 'white', fontSize: 24 }} />
                        </Box>
                    )}
                    {!isMobile && (
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: '#1A1A1A',
                                    letterSpacing: '-0.01em',
                                    lineHeight: 1,
                                }}
                            >
                                {companySettings.companyName}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#606060',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {translate('app.subtitle')}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ flex: 1 }} />

                {/* Right Side Actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Language Switcher */}
                    <LanguageSwitcher />

                    {!isMobile && (
                        <Box sx={{ width: '1px', height: 24, backgroundColor: 'rgba(0,0,0,0.08)', mx: 0.75 }} />
                    )}

                    {/* Notifications */}
                    <IconButton
                        sx={{
                            width: 44,
                            height: 44,
                            '&:hover': {
                                backgroundColor: '#F0F0F0',
                            },
                        }}
                    >
                        <NotificationsIcon sx={{ fontSize: 22, color: '#606060' }} />
                    </IconButton>

                    {!isMobile && (
                        <Box sx={{ width: '1px', height: 24, backgroundColor: 'rgba(0,0,0,0.08)', mx: 0.75 }} />
                    )}

                    {/* User Profile */}
                    <Box
                        onClick={handleMenuOpen}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobile ? 0 : 1.5,
                            px: isMobile ? 1 : 2,
                            py: 1,
                            borderRadius: 3,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: '#F8F9FA',
                            },
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                backgroundColor: '#009A75',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                            }}
                        >
                            {identity?.fullName?.charAt(0) || 'A'}
                        </Avatar>
                        {!isMobile && (
                            <>
                                <Box sx={{ minWidth: 0, maxWidth: 180 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1A1A1A',
                                            lineHeight: 1.2,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {identity?.fullName || 'Admin User'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: '#606060',
                                            fontSize: '0.75rem',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: 'block',
                                        }}
                                    >
                                        {(identity as any)?.roleName ||
                                            translate('menu.roleFallback', { _: 'Administrator' })}
                                    </Typography>
                                </Box>
                                <KeyboardArrowDown sx={{ fontSize: 20, color: '#909090' }} />
                            </>
                        )}
                    </Box>

                    {/* User Menu */}
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        sx={{
                            mt: 1,
                            '& .MuiPaper-root': {
                                minWidth: 200,
                                borderRadius: 2,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid rgba(0,0,0,0.05)',
                            },
                        }}
                    >
                        <MenuItem onClick={handleProfile}>
                            <ListItemIcon>
                                <Person fontSize="small" sx={{ color: '#606060' }} />
                            </ListItemIcon>
                            <ListItemText>{translate('menu.profile')}</ListItemText>
                        </MenuItem>
                        <Divider sx={{ my: 1 }} />
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon>
                                <ExitToApp fontSize="small" sx={{ color: '#FF3D00' }} />
                            </ListItemIcon>
                            <ListItemText sx={{ color: '#FF3D00' }}>{translate('auth.logout')}</ListItemText>
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
};
